<template>
  <div
    style="height: 43vh; width: 750px; overflow-t: scroll; overflow-x: hidden;"
    class="q-py-sm q-mb-lg"
  >
    <div class="row items-end q-col-gutter-md">
      <q-input
        v-model="controlSearchString"
        class="col-6"
        style="min-width: 300px; padding-right: 15px"
        placeholder="Control Search..."
        clearable
        dense
      />
      <div
        v-if="optionsVariables"
        class="col-6"
        style="padding-left: 5px"
      >
        <q-item-label>Option Precedence</q-item-label>
        <q-btn-toggle
          v-model="selectedOptionVariable"
          class="q-my-xs"
          size="sm"
          outline
          toggle-color="primary"
          :options="$_.map(optionsVariables, option => ({ value: option, label: option }))"
        />
      </div>
    </div>
    <div
      v-if="controlSearchString && matchingControls.length"
      class="row justify-between q-mt-sm q-px-md"
      style="border: solid 1px #AAA; border-radius: 5px;"
    >
      <div
        v-for="control in matchingControls"
        :key="control"
        class="col-auto row q-my-sm q-py-xs items-start"
        style="position: relative; min-width: 47.7%"
      >
        <div style="position: absolute; border: solid 1px #DDD; top: -9px; left: -17px; right: -17px; bottom: -8px;" />
        <report-control
          class="col-auto"
          :disable="!isOptionSetOrSetting(control) || finalOptionsVariable === previewOptionsVariable"
          :report="reportElement"
          :options-variable="isOptionSetOrSetting(control) ? finalOptionsVariable : previewOptionsVariable"
          :control="control"
          :inventory-recipes="inventoryRecipes"
          :studios="studios"
          :users="users"
          style="max-width: 90%"
        />
        <q-toggle
          v-if="finalOptionsVariable !== previewOptionsVariable"
          style="position: absolute; top: 0; right: 0"
          :model-value="isOptionSetOrSetting(control)"
          class="col-auto"
          size="xs"
          @update:model-value="toggleControl(control)"
        >
          <q-tooltip>
            {{ isOptionSetOrSetting(control) ? 'Unset value' : 'Set Value' }}
          </q-tooltip>
        </q-toggle>
      </div>
    </div>
    <div
      v-else-if="controlSearchString"
      class="q-pa-lg text-center"
    >
      No Matching Controls
    </div>
    <template v-else>
      <q-tabs
        v-model="tab"
        style="border: solid 1px #AAA; border-bottom: none; border-top-left-radius: 5px; border-top-right-radius: 5px;"
        align="justify"
        dense
        class="bg-grey-4 q-mt-md"
      >
        <q-tab
          v-for="(_controls, category) in categorizedControls"
          :key="category"
          :name="category"
          :label="category"
        />
      </q-tabs>
      <q-tab-panels
        v-model="tab"
        keep-alive
        class="q-mb-xl"
        style="border: solid 1px #AAA; border-bottom-left-radius: 5px; border-bottom-right-radius: 5px;"
      >
        <q-tab-panel
          v-for="(controlsForCategory, category) in categorizedControls"
          :key="category"
          :name="category"
          class="q-py-none no-scroll"
        >
          <div
            class="row justify-between"
            style="width: 100%"
          >
            <div
              v-for="control in controlsForCategory"
              :key="control"
              class="col-auto row q-my-sm q-py-xs items-start"
              style="position: relative; min-width: 47.7%"
            >
              <div style="position: absolute; border: solid 1px #DDD; top: -9px; left: -17px; right: -17px; bottom: -8px;" />
              <report-control
                class="col-auto"
                :disable="!isOptionSetOrSetting(control) || finalOptionsVariable === previewOptionsVariable"
                :report="reportElement"
                :options-variable="finalOptionsForControl(control)"
                :control="control"
                :inventory-recipes="inventoryRecipes"
                :studios="studios"
                :users="users"
                style="max-width: 90%"
              />
              <q-toggle
                v-if="finalOptionsVariable !== previewOptionsVariable"
                style="position: absolute; top: 0; right: 0"
                :model-value="isOptionSetOrSetting(control)"
                class="col-auto"
                size="xs"
                @update:model-value="toggleControl(control)"
              >
                <q-tooltip>
                  {{ isOptionSetOrSetting(control) ? 'Unset value' : 'Set Value' }}
                </q-tooltip>
              </q-toggle>
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </template>
  </div>
</template>

<script>
import { categorizeControls, isOptionSet, optionNamesForControlName, setDefaultOption, unsetOption } from 'src/models/report/Report'
import ReportControl from 'src/modules/admin/report/ReportControl'

export default {
  name: 'ReportControls',
  components: {
    ReportControl
  },
  props: {
    reportElement: {
      type: Object,
      required: false,
      default: null
    },
    optionsVariables: {
      type: Array,
      required: false,
      default: null
    },
    optionsVariable: {
      type: String,
      required: false,
      default: 'reportOptions'
    },
    allControls: {
      type: Boolean,
      required: false,
      default: false
    },
    previewOptionsVariable: {
      type: String,
      required: false,
      default: 'finalOptions'
    }
  },
  data () {
    return {
      shown: false,
      tab: null,
      controlSearchString: null,
      settingControls: [],
      selectedOptionVariable: null
    }
  },
  computed: {
    options () {
      return this.reportElement[this.finalOptionsVariable]
    },
    controls () {
      if (this.allControls) {
        return this.reportElement.allControls
      } else {
        return this.reportElement.controls
      }
    },
    categorizedControls () {
      return categorizeControls(this.controls)
    },
    matchingControls () {
      if (!this.controlSearchString) return []

      const searchString = this.controlSearchString.toLowerCase()
      return _.filter(
        this.controls,
        control =>
          _.includes(control.toLowerCase(), searchString) ||
          _.includes(_.startCase(control).toLowerCase(), searchString)
      )
    },
    finalOptionsVariable () {
      return this.selectedOptionVariable ? `${this.selectedOptionVariable}Options` : this.optionsVariable
    }
  },
  watch: {
    'reportElement.allControls' () {
      this.tab = _.keys(this.categorizedControls)[0]
    }
  },
  models: {
    inventoryRecipes: {
      variables: {
        limit: 0
      }
    },
    studios: {
      variables: {
        limit: 1000
      }
    },
    users: {
      variables () {
        return {
          userRole: 'employees',
          limit: 1000
        }
      }
    }
  },
  created () {
    _.extend(this, { optionNamesForControlName, isOptionSet, unsetOption, categorizeControls })
    this.tab = _.keys(this.categorizedControls)[0]
    if (this.optionsVariables) {
      this.selectedOptionVariable = this.optionsVariables[0]
    }
  },
  methods: {
    isOptionSetOrSetting (control) {
      return isOptionSet(this.options, control) || _.includes(this.settingControls, control)
    },
    toggleControl (control) {
      if (this.isOptionSetOrSetting(control)) {
        this.settingControls = _.without(this.settingControls, control)
        unsetOption(this.options, control)
      } else {
        this.settingControls = [...this.settingControls, control]
        setDefaultOption(this.options, this.reportElement[this.previewOptionsVariable], control)
      }
    },
    finalOptionsForControl (control) {
      return this.isOptionSetOrSetting(control) ? this.finalOptionsVariable : this.previewOptionsVariable
    }
  }
}

</script>

<style lang="scss" scoped>
</style>
